import {DateTime, Duration} from 'luxon';
import React, {FC, ReactNode, useState} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {Dropdown, DropdownProps} from '../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownList} from '../../../../libs/shared/ui/src/components/dropdowns/dropdownList';
import {DropdownItem} from '../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItem';
import {DropdownItemContent} from '../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemContent';
import {SimpleDropdownItem} from '../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown/simpleDropdownItem';
import {CalendarWeekDaysEnum} from '../../../models/api/calendarCommonTypes';
import {DatePicker} from '../../common/dateTime/datePicker';

/*
 * Props.
 */

export interface CutoffTimeProps {
  label: ReactNode;
  trackLabel: string;
  beforeDate: () => number;
}

interface MassArchiveCutoffDropdownProps extends DropdownProps {
  selectedCutoff: CutoffTimeProps;
  onCutoffSelect: (props: CutoffTimeProps) => void;
  showCustom?: boolean;
}

/*
 * Style.
 */

const StyledDiv = styled.div`
  padding: 6px 12px 12px;
`;

/*
 * Component.
 */

export const MassArchiveCutoffDropdown: FC<MassArchiveCutoffDropdownProps> = (props) => {
  const [showDatePicker, setShowDatePicker] = useState(false);
  const {selectedCutoff, onCutoffSelect, showCustom, onRequestClose} = props;

  if (showDatePicker && showCustom) {
    return (
      <Dropdown {...props}>
        <StyledDiv>
          <DatePicker
            maxDate={DateTime.local()}
            selectedDate={DateTime.fromMillis(selectedCutoff.beforeDate())}
            onDateSelect={(date) => {
              onCutoffSelect({
                beforeDate: () => date.toMillis(),
                label: date.toLocaleString(),
                trackLabel: date.toLocaleString(),
              });
              onRequestClose?.();
            }}
            calendarWeekStartDay={CalendarWeekDaysEnum.SUNDAY}
          />
        </StyledDiv>
      </Dropdown>
    );
  }

  return (
    <Dropdown {...props}>
      <DropdownList {...props}>
        {renderCutoffTimeItems(props)}
        {showCustom && (
          <DropdownItem
            onSelect={(e) => {
              e.preventDefault();
              setShowDatePicker(true);
            }}
          >
            <DropdownItemContent>
              <Trans id="massarchive-dayandtime">Day &amp; Time</Trans>
            </DropdownItemContent>
          </DropdownItem>
        )}
      </DropdownList>
    </Dropdown>
  );
};

function renderCutoffTimeItems(props: MassArchiveCutoffDropdownProps) {
  const {selectedCutoff, onCutoffSelect} = props;
  return cutoffTimes.map((c) => (
    <SimpleDropdownItem
      key={c.trackLabel}
      isChecked={selectedCutoff === c}
      onSelect={() => onCutoffSelect(c)}
    >
      {c.label}
    </SimpleDropdownItem>
  ));
}

/*
 * Values.
 */

const beginningOfDay = () => {
  const dt = DateTime.local().startOf('day');
  return dt.toMillis();
};

/** Return X days ago in millis. */
const timeDaysAgo = (days: number) => {
  const dt = DateTime.local();
  const duration = Duration.fromObject({days});
  return dt.minus(duration).toMillis();
};

export const cutoffTimes: ReadonlyArray<CutoffTimeProps> = [
  {
    label: <Trans id="dialogmassarchive-cutofftime-today">Now (all emails)</Trans>,
    trackLabel: 'beforeNow',
    beforeDate: () => new Date().getTime(),
  },
  {
    label: <Trans id="dialogmassarchive-cutofftime-beforetoday">24 hours ago</Trans>,
    trackLabel: '24HoursAgo',
    beforeDate: () => beginningOfDay(),
  },
  {
    label: <Trans id="dialogmassarchive-cutofftime-7daysago">7 days ago</Trans>,
    trackLabel: '7DaysAgo',
    beforeDate: () => timeDaysAgo(7),
  },
  {
    label: <Trans id="dialogmassarchive-cutofftime-30daysago">30 days ago</Trans>,
    trackLabel: '30DaysAgo',
    beforeDate: () => timeDaysAgo(30),
  },
];
